import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { ethers } from 'ethers';
import api from '../../services/api';
import { Icons } from './DashboardLayout';

const TIER_CONFIG = {
  FREE: { label: 'Free', color: 'text-white/60', bg: 'bg-white/5', maxAgents: 2 },
  PRO: { label: 'Pro', color: 'text-blue-400', bg: 'bg-blue-500/10', maxAgents: 5 },
  ENTERPRISE: { label: 'Enterprise', color: 'text-purple-400', bg: 'bg-purple-500/10', maxAgents: 100 },
};

export default function AccountSettingsPage() {
  const { user, token, agents } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKeyInfo, setApiKeyInfo] = useState(null);
  const [newApiKey, setNewApiKey] = useState(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);

  // Load account data (cached)
  useEffect(() => {
    loadAccount();
  }, [token]);

  const loadAccount = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.getAccount(token);
      if (res.success) {
        setAccount(res.data);
      }
      // Load API key info
      const keyRes = await api.getAccountApiKeyInfo(token);
      if (keyRes.success) {
        setApiKeyInfo(keyRes.data);
      }
    } catch (err) {
      console.error('Failed to load account:', err);
    }
    setLoading(false);
  };

  const tier = TIER_CONFIG[account?.account?.tier] || TIER_CONFIG.FREE;
  const limits = account?.limits || { maxAgents: 2, currentAgents: agents.length, remainingAgents: 2 - agents.length };
  const linkedAuth = account?.linkedAuth || { google: !!user?.email, wallet: !!user?.walletAddress, email: !!user?.email };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div 
          className="w-10 h-10 border-2 border-white/10 border-t-white/60 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Account Settings</h1>
        <p className="text-white/50 text-sm mt-1">Manage your account, linked logins, and API access</p>
      </div>

      {/* Profile Section */}
      <ProfileSection account={account} user={user} token={token} onRefresh={loadAccount} />

      {/* Agent Signer Addresses */}
      <AgentSignerSection agents={agents} />

      {/* Tier & Limits */}
      <TierSection tier={tier} limits={limits} />

      {/* Linked Logins */}
      <LinkedLoginsSection 
        linkedAuth={linkedAuth} 
        token={token} 
        onRefresh={loadAccount}
      />

      {/* Account API Key */}
      <AccountApiKeySection 
        token={token}
        apiKeyInfo={apiKeyInfo}
        onRefresh={loadAccount}
      />

      {/* API Key Modal */}
      <AnimatePresence>
        {showApiKeyModal && newApiKey && (
          <ApiKeyModal 
            apiKey={newApiKey}
            credential={newApiKey.credential}
            onClose={() => {
              setShowApiKeyModal(false);
              setNewApiKey(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Generate a deterministic pixel avatar from a string (like GitHub identicons)
function generatePixelAvatar(seed) {
  // Create a simple hash from the seed
  let hash = 0;
  const str = seed || 'default';
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  
  // Use hash to generate colors
  const hue = Math.abs(hash % 360);
  const saturation = 60 + (Math.abs(hash >> 8) % 20);
  const lightness = 45 + (Math.abs(hash >> 16) % 15);
  const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  const fgColor = `hsl(${(hue + 180) % 360}, ${saturation}%, 85%)`;
  
  // Generate 5x5 symmetric pattern
  const pattern = [];
  for (let row = 0; row < 5; row++) {
    const rowPattern = [];
    for (let col = 0; col < 3; col++) {
      const bit = (Math.abs(hash >> (row * 3 + col)) & 1) === 1;
      rowPattern.push(bit);
    }
    // Mirror for symmetry
    pattern.push([...rowPattern, rowPattern[1], rowPattern[0]]);
  }
  
  return { pattern, bgColor, fgColor };
}

// Pixel Avatar Component
function PixelAvatar({ seed, size = 80 }) {
  const { pattern, bgColor, fgColor } = generatePixelAvatar(seed);
  const cellSize = size / 5;
  
  return (
    <div 
      className="rounded-full overflow-hidden flex-shrink-0"
      style={{ width: size, height: size, backgroundColor: bgColor }}
    >
      <svg width={size} height={size} viewBox="0 0 5 5">
        {pattern.map((row, y) =>
          row.map((cell, x) =>
            cell && (
              <rect
                key={`${x}-${y}`}
                x={x}
                y={y}
                width={1}
                height={1}
                fill={fgColor}
              />
            )
          )
        )}
      </svg>
    </div>
  );
}

// Profile Section with editable display name
function ProfileSection({ account, user, token, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const currentName = account?.account?.displayName || user?.displayName || user?.name || 'Developer';
  const email = account?.account?.email || user?.email;
  const walletAddress = account?.account?.walletAddress || user?.walletAddress;
  const avatarSeed = walletAddress || email || currentName;

  useEffect(() => {
    setDisplayName(currentName);
  }, [currentName]);

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError('Display name cannot be empty');
      return;
    }
    
    setSaving(true);
    setError('');
    try {
      const res = await api.updateAccount(token, { displayName: displayName.trim() });
      if (res.success) {
        setEditing(false);
        onRefresh();
      } else {
        setError(res.error?.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('Failed to update profile');
    }
    setSaving(false);
  };

  return (
    <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
      <h2 className="text-lg font-semibold text-white mb-4">Profile</h2>
      
      <div className="flex items-start gap-5">
        {/* Pixel Avatar */}
        <PixelAvatar seed={avatarSeed} size={80} />
        
        <div className="flex-1 space-y-4">
          {/* Display Name */}
          <div>
            <label className="block text-sm text-white/50 mb-1">Display Name</label>
            {editing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-black border border-white/10 text-white outline-none focus:border-white/20 transition-colors"
                  placeholder="Enter display name"
                  data-testid="display-name-input"
                />
                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditing(false);
                      setDisplayName(currentName);
                      setError('');
                    }}
                    className="px-4 py-2 rounded-lg text-white/60 bg-white/[0.05] hover:bg-white/[0.08] text-sm"
                  >
                    Cancel
                  </button>
                  <motion.button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-white text-black font-medium text-sm disabled:opacity-50"
                    whileTap={{ scale: 0.98 }}
                    data-testid="save-display-name-btn"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </motion.button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <p className="text-white font-medium">{currentName}</p>
                <button
                  onClick={() => setEditing(true)}
                  className="px-3 py-1 rounded-lg text-xs font-medium text-white/60 bg-white/[0.05] hover:bg-white/[0.08] transition-colors"
                  data-testid="edit-display-name-btn"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          
          {/* Email */}
          {email && (
            <div>
              <label className="block text-sm text-white/50 mb-1">Email</label>
              <p className="text-white font-medium">{email}</p>
            </div>
          )}
          
          {/* Wallet Address */}
          {walletAddress && (
            <div>
              <label className="block text-sm text-white/50 mb-1">Wallet Address</label>
              <code className="text-white/70 font-mono text-sm">{walletAddress}</code>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Agent Signer Addresses Section
function AgentSignerSection({ agents }) {
  const [copiedAddress, setCopiedAddress] = useState(null);

  const handleCopy = (address) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  // Get unique signer addresses from all agents
  const signerAddresses = agents
    .filter(agent => agent.wallets?.length > 0)
    .map(agent => ({
      agentName: agent.name,
      agentId: agent.id,
      turnkeyAddress: agent.wallets[0]?.turnkeyAddress,
      smartAccountAddress: agent.wallets[0]?.address,
      chain: agent.wallets[0]?.chain || 'base'
    }))
    .filter(item => item.turnkeyAddress);

  if (signerAddresses.length === 0) {
    return null;
  }

  return (
    <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Agent Signer Addresses</h2>
          <p className="text-white/50 text-sm mt-1">EOA addresses that pay gas fees for your agents</p>
        </div>
      </div>
      
      <div className="p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/10 mb-4">
        <p className="text-yellow-400/80 text-sm">
          These addresses need ETH to pay for gas when executing transactions. Keep them funded to ensure your agents can operate.
        </p>
      </div>

      <div className="space-y-3">
        {signerAddresses.map((item) => (
          <div 
            key={item.agentId}
            className="p-4 rounded-xl bg-black/30 border border-white/[0.04]"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium">{item.agentName}</span>
              <span className="text-white/40 text-xs uppercase">{item.chain}</span>
            </div>
            
            {/* Smart Account Address */}
            <div className="mb-2">
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Smart Account</p>
              <div className="flex items-center gap-2">
                <code className="text-white/60 font-mono text-xs flex-1 truncate">
                  {item.smartAccountAddress}
                </code>
                <button
                  onClick={() => handleCopy(item.smartAccountAddress)}
                  className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-all"
                >
                  {copiedAddress === item.smartAccountAddress ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-400">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <Icons.Copy size={14} />
                  )}
                </button>
              </div>
            </div>

            {/* Signer EOA Address */}
            <div className="p-2.5 rounded-lg bg-yellow-500/5 border border-yellow-500/10">
              <p className="text-yellow-400/70 text-[10px] uppercase tracking-wider mb-1">Signer EOA (Pays Gas)</p>
              <div className="flex items-center gap-2">
                <code className="text-yellow-400 font-mono text-xs flex-1 truncate">
                  {item.turnkeyAddress}
                </code>
                <button
                  onClick={() => handleCopy(item.turnkeyAddress)}
                  className="px-2.5 py-1 rounded-lg bg-yellow-500/10 text-yellow-400 text-xs font-medium hover:bg-yellow-500/20 transition-colors"
                  data-testid={`copy-signer-${item.agentId}`}
                >
                  {copiedAddress === item.turnkeyAddress ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Tier Section
function TierSection({ tier, limits }) {
  return (
    <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Plan & Limits</h2>
        <span className={`px-3 py-1 rounded-lg text-sm font-medium ${tier.color} ${tier.bg}`}>
          {tier.label}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-black/30">
          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Max Agents</p>
          <p className="text-2xl font-bold text-white">{limits.maxAgents}</p>
        </div>
        <div className="p-4 rounded-xl bg-black/30">
          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Current</p>
          <p className="text-2xl font-bold text-white">{limits.currentAgents}</p>
        </div>
        <div className="p-4 rounded-xl bg-black/30">
          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Remaining</p>
          <p className="text-2xl font-bold text-green-400">{limits.remainingAgents}</p>
        </div>
      </div>
      
      {tier.label === 'Free' && (
        <p className="text-white/40 text-sm mt-4">
          Upgrade to Pro for up to 5 agents and advanced features.
        </p>
      )}
    </div>
  );
}

// Linked Logins Section
function LinkedLoginsSection({ linkedAuth, token, onRefresh }) {
  const [linking, setLinking] = useState(null);
  const [error, setError] = useState(null);

  // Google link handler
  const googleLink = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLinking('google');
      setError(null);
      try {
        const res = await api.linkGoogle(token, tokenResponse.access_token);
        if (res.success) {
          onRefresh();
        } else {
          setError(res.error?.message || 'Failed to link Google');
        }
      } catch (err) {
        setError('Failed to link Google');
      }
      setLinking(null);
    },
    onError: () => {
      setError('Google authentication failed');
      setLinking(null);
    },
    flow: 'implicit',
  });

  // Wallet link handler
  const handleLinkWallet = async () => {
    setLinking('wallet');
    setError(null);
    
    try {
      if (!window.ethereum) {
        throw new Error('No wallet detected');
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send('eth_requestAccounts', []);
      const signer = provider.getSigner();
      const walletAddress = await signer.getAddress();

      const res = await api.linkWallet(token, walletAddress);
      if (res.success) {
        onRefresh();
      } else {
        setError(res.error?.message || 'Failed to link wallet');
      }
    } catch (err) {
      setError(err.message || 'Failed to link wallet');
    }
    setLinking(null);
  };

  return (
    <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
      <h2 className="text-lg font-semibold text-white mb-4">Linked Logins</h2>
      <p className="text-white/50 text-sm mb-4">Connect multiple login methods to your account</p>
      
      {error && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 mb-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {/* Google */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-black/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
              </svg>
            </div>
            <div>
              <p className="text-white font-medium">Google</p>
              <p className="text-white/40 text-sm">
                {linkedAuth.google ? 'Connected' : 'Not connected'}
              </p>
            </div>
          </div>
          {linkedAuth.google ? (
            <span className="px-3 py-1.5 rounded-lg text-sm font-medium text-green-400 bg-green-500/10">
              ✓ Linked
            </span>
          ) : (
            <button
              onClick={() => googleLink()}
              disabled={linking === 'google'}
              className="px-4 py-2 rounded-lg bg-white/[0.05] border border-white/[0.08] text-white text-sm hover:bg-white/[0.08] transition-colors disabled:opacity-50"
            >
              {linking === 'google' ? 'Linking...' : 'Link Google'}
            </button>
          )}
        </div>

        {/* Wallet */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-black/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
              <Icons.Wallets size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-white font-medium">Wallet</p>
              <p className="text-white/40 text-sm">
                {linkedAuth.wallet ? 'Connected' : 'Not connected'}
              </p>
            </div>
          </div>
          {linkedAuth.wallet ? (
            <span className="px-3 py-1.5 rounded-lg text-sm font-medium text-green-400 bg-green-500/10">
              ✓ Linked
            </span>
          ) : (
            <button
              onClick={handleLinkWallet}
              disabled={linking === 'wallet'}
              className="px-4 py-2 rounded-lg bg-white/[0.05] border border-white/[0.08] text-white text-sm hover:bg-white/[0.08] transition-colors disabled:opacity-50"
            >
              {linking === 'wallet' ? 'Linking...' : 'Link Wallet'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Account API Key Section
function AccountApiKeySection({ token, apiKeyInfo, onRefresh }) {
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  const hasKey = apiKeyInfo?.hasKey || false;
  const keyPrefix = apiKeyInfo?.prefix;

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await api.generateAccountApiKey(token);
      if (res.success) {
        setNewKey(res.data);
        setShowModal(true);
        onRefresh();
      } else {
        setError(res.error?.message || 'Failed to generate key');
      }
    } catch (err) {
      setError('Failed to generate key');
    }
    setGenerating(false);
  };

  const handleRevoke = async () => {
    if (!confirm('Are you sure you want to revoke your account API key? This cannot be undone.')) {
      return;
    }
    setRevoking(true);
    setError(null);
    try {
      const res = await api.revokeAccountApiKey(token);
      if (res.success) {
        onRefresh();
      } else {
        setError(res.error?.message || 'Failed to revoke key');
      }
    } catch (err) {
      setError('Failed to revoke key');
    }
    setRevoking(false);
  };

  return (
    <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
      <h2 className="text-lg font-semibold text-white mb-2">Account API Key</h2>
      <p className="text-white/50 text-sm mb-4">
        Generate a downloadable API key for programmatic access to your entire account
      </p>

      {error && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 mb-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {hasKey ? (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-black/30 flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Active Key</p>
              <code className="text-white/50 text-sm font-mono">{keyPrefix}...****</code>
            </div>
            <button
              onClick={handleRevoke}
              disabled={revoking}
              className="px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm hover:bg-red-500/20 transition-colors disabled:opacity-50"
            >
              {revoking ? 'Revoking...' : 'Revoke Key'}
            </button>
          </div>
          <p className="text-white/40 text-xs">
            Use header: <code className="text-white/60">x-account-key: your_key</code>
          </p>
        </div>
      ) : (
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
        >
          {generating ? 'Generating...' : 'Generate API Key'}
        </button>
      )}

      {/* API Key Modal */}
      <AnimatePresence>
        {showModal && newKey && (
          <ApiKeyModal 
            apiKey={newKey.apiKey}
            credential={newKey.credential}
            onClose={() => {
              setShowModal(false);
              setNewKey(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// API Key Modal
function ApiKeyModal({ apiKey, credential, onClose }) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(credential, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'aibl-account-credentials.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-yellow-500/20 bg-yellow-500/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-yellow-400">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Save Your Account API Key</h2>
              <p className="text-yellow-400/80 text-sm">This key will only be shown once</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* API Key */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Account API Key</p>
            <div className="p-4 rounded-xl bg-black border border-white/[0.08] font-mono text-sm break-all">
              <code className="text-green-400" data-testid="api-key-display">{apiKey}</code>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <motion.button
              onClick={handleCopy}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white text-sm hover:bg-white/[0.08] transition-colors"
              whileTap={{ scale: 0.98 }}
              data-testid="copy-api-key-btn"
            >
              {copied ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-400">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <Icons.Copy size={16} />
                  Copy Key
                </>
              )}
            </motion.button>
            <motion.button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white text-sm hover:bg-white/[0.08] transition-colors"
              whileTap={{ scale: 0.98 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              Download JSON
            </motion.button>
          </div>

          {/* Warning */}
          <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
            <p className="text-red-400 text-sm">
              This API key provides full access to your account. Store it securely and never share it publicly.
            </p>
          </div>

          {/* Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-white/20 bg-black text-white focus:ring-white/20"
            />
            <span className="text-white/70 text-sm">
              I have saved my API key and understand it will not be shown again
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06]">
          <motion.button
            onClick={onClose}
            disabled={!acknowledged}
            className="w-full px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-30 disabled:cursor-not-allowed"
            whileHover={acknowledged ? { scale: 1.02 } : {}}
            whileTap={acknowledged ? { scale: 0.98 } : {}}
          >
            Done
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
